import type { REST_schema } from "../../@types/sharedSchema";

/**
 * ONE MODULE STORE FOR "WHO HANDLES THIS PERSON", SHARED BY EVERY SURFACE ON THE SCREEN.
 *
 * ── Why this is a module store and `useCrmHolders`' cache is per-instance ───────
 *
 * That difference is deliberate, and it is about the PAYLOAD rather than about taste.
 *
 * A holders answer is redacted per request: the server projects different columns for
 * `pool: 'basic'` than for `full`, and `agencyPersonModel` is the second lock on the same
 * tier. Caching it beyond its query would risk showing a `basic` viewer an answer computed
 * for someone else, so it resets with the list's `resetKey`.
 *
 * A designation is one `userId` behind ONE flat gate — org membership — with no tier and
 * nothing to redact. So it is cacheable per `(orgId, contactId)` for the whole session, and
 * that is exactly what lets the CRM list, the new-contact modal, the contact picker, the
 * signature form and the side panel read one answer instead of issuing four requests for the
 * same phone. Contact ids are phone numbers and are stable — the same property
 * `useCrmHolders` already relies on.
 *
 * ── What can go stale, and why that is the right trade ──────────────────────────
 *
 * A colleague reassigning while this tab is open leaves a stale badge until a reload or
 * until the assign dialog writes through. That is the same staleness the holders cache
 * already accepts, for a fact that changes far less often than a status or a note.
 *
 * ── NOT `persistedCache` ────────────────────────────────────────────────────────
 *
 * That is for slow-changing SESSION-level answers — the agency roster, the caller's
 * permissions — which are one value each and are worth painting from on a cold load. A
 * per-contact designation is neither: persisting thousands of them would trade a real
 * correctness risk (a designation that moved while the tab was closed) for nothing, since
 * the badge is an enrichment that lands a beat after the list either way.
 */

type Assignment = REST_schema.CrmContactAssignment;

/** `null` = answered, nobody designated. Absent from the map = not answered yet. */
type Entry = Assignment[] | null;

const cache = new Map<string, Entry>();
/** Ids a request is already in flight for, so two surfaces do not ask the same question. */
const inFlight = new Set<string>();
const subscribers = new Set<() => void>();

/** The cache key. `orgId` is part of it: two agencies genuinely have different answers. */
export const handlerCacheKey = (orgId: string | undefined, contactId: string): string => `${orgId ?? ""}|${contactId}`;

export const subscribeToContactHandlers = (listener: () => void): (() => void) => {
  subscribers.add(listener);
  return () => {
    subscribers.delete(listener);
  };
};

const notify = (): void => subscribers.forEach((listener) => listener());

export const readContactHandlers = (orgId: string | undefined, contactId: string): Entry | undefined =>
  cache.get(handlerCacheKey(orgId, contactId));

/** Which of these ids nobody has asked about yet. Marks them in flight in the same pass. */
export const claimUnknownContactIds = (orgId: string | undefined, contactIds: readonly string[]): string[] => {
  const claimed: string[] = [];
  for (const contactId of contactIds) {
    const key = handlerCacheKey(orgId, contactId);
    if (cache.has(key) || inFlight.has(key)) continue;
    inFlight.add(key);
    claimed.push(contactId);
  }
  return claimed;
};

/**
 * Records an answer for every id that was asked about — including the ones the server said
 * nothing about, which are cached as `null` ("nobody is designated") so a later page does
 * not re-ask. Same rule as the holders cache defaulting an unmentioned count to 1.
 */
export const recordContactHandlers = (
  orgId: string | undefined,
  askedIds: readonly string[],
  answered: Record<string, Assignment[]>
): void => {
  for (const contactId of askedIds) {
    const key = handlerCacheKey(orgId, contactId);
    inFlight.delete(key);
    const value = answered[contactId];
    cache.set(key, value && value.length > 0 ? value : null);
  }
  notify();
};

/**
 * A request failed. The ids are RELEASED rather than cached as "nobody".
 *
 * The distinction the badge is built on: an unanswered id must stay `undefined` so the badge
 * renders a reserved blank, never an affirmative "nobody is designated". Releasing them also
 * means the next page load retries — which is the retry, since hammering an enrichment
 * endpoint on every render is not.
 */
export const releaseContactIds = (orgId: string | undefined, contactIds: readonly string[]): void => {
  for (const contactId of contactIds) inFlight.delete(handlerCacheKey(orgId, contactId));
};

/**
 * Write-through after the assign dialog succeeds, so the badge repaints before any refetch.
 *
 * Writes the SAME shape the server would have returned, so nothing downstream can tell the
 * difference between an optimistic value and a fetched one.
 */
export const setContactHandlerLocally = (orgId: string | undefined, contactId: string, assignment: Assignment | null): void => {
  cache.set(handlerCacheKey(orgId, contactId), assignment ? [assignment] : null);
  notify();
};

/** Sign-out, and the org switch — a cache keyed by org must not outlive the account. */
export const clearContactHandlers = (): void => {
  cache.clear();
  inFlight.clear();
  notify();
};
